/**
 * `<caos-progress-bar>` — determinate or staged progress for a long operation.
 * (Catalog: Status & feedback › ProgressBar — determinate or staged progress for a long operation.)
 *
 * Determinate by default: `value` between `min` (0) and `max` (100) fills the track,
 * and the element carries `role="progressbar"` with `aria-valuenow/min/max`.
 * `indeterminate` drops `aria-valuenow` and animates a sliding fill — suppressed under
 * `prefers-reduced-motion` (it falls back to a static striped track so a reduced-motion
 * reader still sees "in progress"). An optional `intent` (accent by default; success /
 * warning / danger) colours the fill; the fill accent moves with a brand override while
 * the semantic intents stay fixed. An optional visible `label` and `show-value` sit on
 * the NEUTRAL surface (neutral text tokens, ≥4.5:1).
 *
 * CAOS-601 — `variant="circular"` draws the SAME measurement as a ring instead of a bar. The
 * incumbent ships a separate circular-progress component while its own progress bar already carries
 * a circular variant: the same idea expressed twice, because nothing stopped it. A ring is a shape
 * this bar takes, not a second component — so the value, the bounds, the intents, the
 * indeterminate state, the reduced-motion fallback and the `role="progressbar"` contract are the
 * ones already here, and a page that switches shape keeps every one of them.
 *
 * Styling seams: `::part(bar)`, `::part(header)`, `::part(label)`, `::part(value)`,
 * `::part(track)`, `::part(fill)`, `::part(ring)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

export type CaosProgressIntent = 'accent' | 'success' | 'warning' | 'danger';

/** The shapes the same measurement can take. */
export type CaosProgressVariant = 'bar' | 'circular';

/**
 * The ring is drawn with `pathLength="100"`, so its dash lengths ARE percentages and the arithmetic
 * below is the percentage itself rather than a circumference nobody can check by eye. The viewBox
 * and the radius are the drawing's own units and never reach the page: the rendered diameter comes
 * from the box the ring is given.
 */
const RING_LENGTH = 100;
/** The sliding segment an indeterminate ring shows, matching the bar's 35% segment. */
const RING_INDETERMINATE_ARC = 35;

const sheet = makeSheet(`
  :host { display: block; }

  .bar { display: flex; flex-direction: column; gap: var(--caos-space-2); }

  .header { display: flex; align-items: baseline; justify-content: space-between; gap: var(--caos-space-3); }
  .header:not(:has(.label:not(:empty), .value:not(:empty))) { display: none; }

  .label {
    font-family: var(--caos-font);
    font-size: 12.5px;
    font-weight: 500;
    line-height: 1.4;
    color: var(--caos-color-text);
  }
  .label:empty { display: none; }
  .value {
    font-family: var(--caos-font);
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    color: var(--caos-color-text-muted);
  }
  .value:empty { display: none; }

  .track {
    position: relative;
    width: 100%;
    height: 8px;
    border-radius: var(--caos-radius-pill);
    background: var(--caos-color-surface-2);
    overflow: hidden;
  }

  /* ── the circular variant ─────────────────────────────────────────────────────────────────────
     The ring is square and takes the inline size it is given, capped so it cannot run away inside
     a wide column. The cap is a plain length, as every other cap in the library is, and deliberately
     NOT a step of the platform size scale: that scale exists for the box an icon draws in, the
     diameter of an avatar and the height of a control, and a progress ring is none of the three.
     The bar does not take its own height from the scale either — its track is a flat 8px — so
     reaching for a step here would have been a fourth use of a decision that named three, and would
     have let a theme's control heights silently resize a progress ring. The track's own bar styling
     is undone rather than conditionally applied, so the determinate/indeterminate rules above stay
     written once. */
  :host([variant="circular"]) .bar { align-items: center; }
  :host([variant="circular"]) .header { width: 100%; }
  :host([variant="circular"]) .track {
    width: 100%;
    max-width: 56px;
    height: auto;
    aspect-ratio: 1;
    border-radius: 50%;
    background: transparent;
    overflow: visible;
  }
  /* One shape is drawn at a time: the bar's fill is not in a circular progress bar's picture, and
     the ring is not in a bar's. Both are built once and neither is rebuilt when the shape changes. */
  :host([variant="circular"]) .fill { display: none; }
  :host(:not([variant="circular"])) .ring { display: none; }
  :host(:not([variant="circular"])) .ring-value { display: none; }

  .ring { display: block; width: 100%; height: 100%; transform: rotate(-90deg); }
  .ring-track { fill: none; stroke: var(--caos-color-surface-2); stroke-width: 4; }
  .ring-fill {
    fill: none;
    stroke: var(--caos-color-accent);
    stroke-width: 4;
    stroke-linecap: round;
    transition: stroke-dashoffset 0.3s ease;
  }
  :host([intent="success"]) .ring-fill { stroke: var(--caos-color-success); }
  :host([intent="warning"]) .ring-fill { stroke: var(--caos-color-warning); }
  :host([intent="danger"]) .ring-fill { stroke: var(--caos-color-danger); }

  /* The percentage belongs in the middle of a ring, not beside it — so the header's copy is stood
     down and this one takes over. Only one of them is ever drawn. */
  .ring-value {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    font-family: var(--caos-font);
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    color: var(--caos-color-text-muted);
  }
  .ring-value:empty { display: none; }
  :host([variant="circular"]) .header .value { display: none; }

  /* Indeterminate — a short arc going round, the ring's answer to the bar's sliding segment. */
  :host([variant="circular"][indeterminate]) .ring {
    animation: caos-progress-spin 1.4s linear infinite;
  }
  :host([variant="circular"][indeterminate]) .ring-fill { transition: none; }
  @keyframes caos-progress-spin {
    from { transform: rotate(-90deg); }
    to   { transform: rotate(270deg); }
  }

  .fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 0%;
    border-radius: inherit;
    background: var(--caos-color-accent);
    transition: width 0.3s ease;
  }
  :host([intent="success"]) .fill { background: var(--caos-color-success); }
  :host([intent="warning"]) .fill { background: var(--caos-color-warning); }
  :host([intent="danger"]) .fill { background: var(--caos-color-danger); }

  /* indeterminate — a sliding segment; width fixed, position animated */
  :host([indeterminate]) .fill {
    width: 35%;
    transition: none;
    animation: caos-progress-slide 1.4s ease-in-out infinite;
  }
  @keyframes caos-progress-slide {
    0%   { left: -35%; }
    100% { left: 100%; }
  }

  /* reduced motion — no slide; a static striped track still reads "in progress" */
  @media (prefers-reduced-motion: reduce) {
    /* The ring's version of the same answer: nothing moves, and a complete dimmed ring says that
       something is under way without claiming to know how far along it is. A still arc frozen at
       one angle would claim a position it does not have. */
    :host([variant="circular"][indeterminate]) .ring { animation: none; }
    :host([variant="circular"][indeterminate]) .ring-fill {
      stroke-dasharray: none;
      stroke-dashoffset: 0;
      opacity: 0.55;
    }
    :host([indeterminate]) .fill {
      animation: none;
      left: 0;
      width: 100%;
      background: repeating-linear-gradient(
        45deg,
        var(--caos-color-accent) 0,
        var(--caos-color-accent) 8px,
        color-mix(in srgb, var(--caos-color-accent) 45%, transparent) 8px,
        color-mix(in srgb, var(--caos-color-accent) 45%, transparent) 16px
      );
      opacity: 0.85;
    }
    :host([indeterminate][intent="success"]) .fill { background: var(--caos-color-success); }
    :host([indeterminate][intent="warning"]) .fill { background: var(--caos-color-warning); }
    :host([indeterminate][intent="danger"]) .fill { background: var(--caos-color-danger); }
  }
`);

export class CaosProgressBar extends CaosElement {
  static get observedAttributes(): string[] {
    return ['value', 'min', 'max', 'indeterminate', 'label', 'show-value', 'intent', 'variant'];
  }

  private labelEl: HTMLSpanElement;
  private valueEl: HTMLSpanElement;
  private fill: HTMLDivElement;
  /** Assigned by `buildRing`, which the constructor calls — the definite-assignment mark says so. */
  private ringFill!: SVGCircleElement;
  private ringValueEl: HTMLSpanElement;

  constructor() {
    super(sheet);

    const bar = document.createElement('div');
    bar.className = 'bar';
    bar.setAttribute('part', 'bar');

    const header = document.createElement('div');
    header.className = 'header';
    header.setAttribute('part', 'header');
    this.labelEl = document.createElement('span');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');
    this.valueEl = document.createElement('span');
    this.valueEl.className = 'value';
    this.valueEl.setAttribute('part', 'value');
    header.append(this.labelEl, this.valueEl);

    const track = document.createElement('div');
    track.className = 'track';
    track.setAttribute('part', 'track');
    this.fill = document.createElement('div');
    this.fill.className = 'fill';
    this.fill.setAttribute('part', 'fill');

    const ring = this.buildRing();
    this.ringValueEl = document.createElement('span');
    this.ringValueEl.className = 'ring-value';
    this.ringValueEl.setAttribute('aria-hidden', 'true');

    track.append(this.fill, ring, this.ringValueEl);

    bar.append(header, track);
    this.root.append(bar);
  }

  /**
   * The ring, built through the DOM like everything else that draws (no `innerHTML`, so a strict
   * content-security policy holds). It is NOT a glyph and takes no colour from `currentColor`: the
   * stroke reads the same intent tokens the bar's fill does, so one brand override moves both.
   */
  private buildRing(): SVGSVGElement {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('class', 'ring');
    svg.setAttribute('part', 'ring');
    svg.setAttribute('viewBox', '0 0 36 36');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    const circle = (cls: string): SVGCircleElement => {
      const c = document.createElementNS(ns, 'circle');
      c.setAttribute('class', cls);
      c.setAttribute('cx', '18');
      c.setAttribute('cy', '18');
      c.setAttribute('r', '16');
      c.setAttribute('pathLength', String(RING_LENGTH));
      return c;
    };

    this.ringFill = circle('ring-fill');
    svg.append(circle('ring-track'), this.ringFill);
    return svg;
  }

  connectedCallback(): void {
    this.setAttribute('role', 'progressbar');
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    const min = this.num('min', 0);
    const max = this.num('max', 100);
    const indeterminate = this.hasAttribute('indeterminate');

    this.setAttribute('aria-valuemin', String(min));
    this.setAttribute('aria-valuemax', String(max));

    if (indeterminate) {
      this.removeAttribute('aria-valuenow');
      this.valueEl.textContent = '';
      this.ringValueEl.textContent = '';
      // A short arc, spun by the stylesheet. The same 35% the bar's sliding segment uses, so the
      // two shapes of one indeterminate state read as the same amount of "something is happening".
      this.ringFill.setAttribute(
        'stroke-dasharray',
        `${RING_INDETERMINATE_ARC} ${RING_LENGTH - RING_INDETERMINATE_ARC}`,
      );
      this.ringFill.setAttribute('stroke-dashoffset', '0');
    } else {
      const raw = this.num('value', min);
      const clamped = Math.min(max, Math.max(min, raw));
      const span = max - min || 1;
      const pct = ((clamped - min) / span) * 100;
      this.fill.style.width = `${pct}%`;
      // `pathLength` is 100, so the dash lengths ARE the percentage — the drawn arc is the number.
      this.ringFill.setAttribute('stroke-dasharray', String(RING_LENGTH));
      this.ringFill.setAttribute('stroke-dashoffset', String(RING_LENGTH - pct));
      this.setAttribute('aria-valuenow', String(clamped));
      const shown = this.hasAttribute('show-value') ? `${Math.round(pct)}%` : '';
      this.valueEl.textContent = shown;
      this.ringValueEl.textContent = shown;
    }

    this.labelEl.textContent = this.getAttribute('label') ?? '';
    const label = this.getAttribute('label');
    if (label) this.setAttribute('aria-label', label);
  }

  private num(attr: string, fallback: number): number {
    const v = parseFloat(this.getAttribute(attr) ?? '');
    return Number.isFinite(v) ? v : fallback;
  }
}

define('caos-progress-bar', CaosProgressBar);

declare global {
  interface HTMLElementTagNameMap {
    'caos-progress-bar': CaosProgressBar;
  }
}
